/**
 * Safe date helpers.
 *
 * Every page that renders a date from the API used to do:
 *
 *     format(new Date(row.invoice_date + "T12:00:00"), "MMM d, yyyy")
 *
 * If `row.invoice_date` was truthy but malformed (e.g. legacy import,
 * empty whitespace, weird locale string), `new Date(...)` returned an
 * Invalid Date object and `date-fns` threw `RangeError: Invalid time
 * value`, blowing up the whole page via the React ErrorBoundary.
 *
 * Use these helpers EVERYWHERE we render or parse a server date, so a
 * single bad row can never crash a page again.
 */
import { format, formatDistanceToNow, isValid, parseISO } from "date-fns";

/** Parse a value to a Date or return null when it isn't a real date.
 *  Accepts: ISO strings ("2025-06-30", "2025-06-30T12:00:00Z"), Date
 *  instances, or millisecond timestamps. Returns null for "", null,
 *  undefined, and any unparseable string. */
export function toDate(value) {
  if (value === null || value === undefined || value === "") return null;
  if (value instanceof Date) return isValid(value) ? value : null;
  if (typeof value === "number") {
    const d = new Date(value);
    return isValid(d) ? d : null;
  }
  if (typeof value === "string") {
    // Try ISO first (handles "2025-06-30" and full ISO timestamps).
    let d = parseISO(value);
    if (isValid(d)) return d;
    // Fallback to Date's native parser (handles "Jun 30 2025" etc).
    d = new Date(value);
    return isValid(d) ? d : null;
  }
  return null;
}

/** Format a server-side date safely. Returns `fallback` (default "") if
 *  the value can't be parsed instead of throwing. */
export function safeFormatDate(value, fmt = "MMM d, yyyy", fallback = "") {
  const d = toDate(value);
  return d ? format(d, fmt) : fallback;
}

/** Convenience for date-only strings ("2025-06-30") that should be
 *  rendered as the noon-of-that-day to avoid timezone day-shifting.
 *  Falls back to the raw value as a Date if it already includes time. */
export function safeFormatDateOnly(value, fmt = "MMM d, yyyy", fallback = "") {
  if (!value) return fallback;
  // If it's a pure date string (no "T"), pin it to local noon so the
  // formatter doesn't shift it across timezones.
  const v = typeof value === "string" && !value.includes("T")
    ? `${value}T12:00:00`
    : value;
  return safeFormatDate(v, fmt, fallback);
}

/** Returns `true` when the value can be parsed as a Date. */
export function isValidDate(value) {
  return toDate(value) !== null;
}

/** Safely render a "5 minutes ago" style relative string. Returns
 *  `fallback` (default "—") for any unparseable date. */
export function safeDistanceToNow(value, opts = {}, fallback = "") {
  const d = toDate(value);
  return d ? formatDistanceToNow(d, opts) : fallback;
}
